// Sites page rules. Words: SITES_PAGE_WORDS.md and DEMO_SET_WORDS.md (approved).
// Mockup: MOCKUP_SITES_APPROVED.html. Data names: REGISTRY_COPY_WORDS.md.
// Pure: no database. Shared by the page, the server steps (functions/src/sites.ts)
// and the unit tests, so the page and the server refuse the same things.

import { isSiteZone } from "./site-time";

// ---- What Firestore holds ----

/** companies/{company}/sites/{siteNumber}. Written by the registry copy, then the Sites page. */
export interface Site {
  siteNumber: string;
  name: string;
  city: string;
  state: string;
  isActive: boolean;
  /** YYYY-MM-DD. Only 00 has one today. */
  arcFlashAssessedOn?: string | null;
  /** The plant's own clock (SITE_TIME_ZONE_WORDS.md). Absent on sites made before zones: siteZone() works it out. */
  timeZone?: string | null;
  /** The site's stage as of the last hub look with signal (STAGE_CLOUD_WORDS.md). Server writes it. */
  stage?: SiteStageRecord | null;
  /** Steps 00 to 04 the admin marked Done on the hub (COHESION_CHANGE_LOG.md change 2). Server writes it. */
  stepsDone?: Record<string, unknown> | null;
}

/** Saved on the site by server call 99 saveSiteStage. */
export interface SiteStageRecord {
  /** Nine letters, steps 00 to 08: g done, y in progress, n not started. */
  strip: string;
  savedAt: string;
  savedBy: string;
  savedByName: string;
}

/** A stage strip the server will save: nine letters, g y n. */
export function isStageStrip(v: unknown): v is string {
  return typeof v === "string" && /^[gyn]{9}$/.test(v);
}

export interface UsedFor {
  alerts: boolean;
  escalation: boolean;
  eewpApprover: boolean;
  lotoAwareness: boolean;
  /** Told when an EEWP is requested at the site. No decision, no limit (EEWP_WORDS.md rev 3). */
  eewpAwareness?: boolean;
  /** Confirms LOTO dates QEWs report at this site (QEW_DASHBOARD_WORDS.md). Absent on copied contacts. */
  lotoVerifier?: boolean;
}

/** companies/{company}/contacts/{CON-NNN}. Program and site contacts share the collection. */
export interface Contact {
  contactId: string;
  scope: "site" | "program";
  siteNumber?: string;
  name: string;
  email: string;
  phone?: string;
  role: string;
  isActive: boolean;
  usedFor: UsedFor;
}

/** One row of the read-only asset list. companies/{company}/sites/{site}/assets/{assetId}. */
export interface AssetRow {
  assetId: string;
  location: string;
  equipmentType: string;
  isRemoved?: boolean;
}

// ---- Set lists ----

/** The role list from the words. The first three are the EEWP approver slots. */
export const CONTACT_ROLES = [
  "Plant Manager",
  "Maintenance / Engineering Manager",
  "Safety Manager",
  "Supervisor",
  "Electrically Knowledgeable Person",
  "Other",
] as const;

export const USED_FOR: { key: keyof UsedFor; label: string; hint: string }[] = [
  { key: "alerts", label: "Alerts", hint: "JSP, observations, training, gloves" },
  { key: "escalation", label: "Escalation", hint: "Visibility or approval when a workflow calls for it" },
  { key: "eewpApprover", label: "EEWP Approver", hint: "Must say yes on every energized work permit here. Up to 3. A supervisor or manager, never a QEW" },
  { key: "eewpAwareness", label: "EEWP Awareness", hint: "An email when an EEWP is requested. No decision. No limit" },
  { key: "lotoAwareness", label: "LOTO Awareness", hint: "Told when electrical work starts at the site" },
  { key: "lotoVerifier", label: "LOTO Verifier", hint: "Confirms the LOTO dates QEWs report at this site" },
];

/** Picking Plant Manager ticks Alerts and Escalation, matching what the plant manager gets today. */
export function usedForForRole(role: string, current: UsedFor): UsedFor {
  return role === "Plant Manager" ? { ...current, alerts: true, escalation: true } : current;
}

export const NO_TICKS: UsedFor = { alerts: false, escalation: false, eewpApprover: false, eewpAwareness: false, lotoAwareness: false, lotoVerifier: false };

// ---- Site list ----

export function sortSites(sites: Site[]): Site[] {
  return [...sites].sort((a, b) => a.siteNumber.localeCompare(b.siteNumber));
}

export function siteContacts(contacts: Contact[], siteNumber: string): Contact[] {
  return contacts
    .filter((c) => c.scope === "site" && c.siteNumber === siteNumber)
    .sort((a, b) => Number(b.isActive) - Number(a.isActive) || a.name.localeCompare(b.name));
}

/** An active Plant Manager contact at the site. */
export function hasPlantManager(contacts: Contact[], siteNumber: string): boolean {
  return siteContacts(contacts, siteNumber).some((c) => c.isActive && c.role === "Plant Manager");
}

/** The warning flag: only an Active site with no active Plant Manager. */
export function needsPlantManager(site: Site, contacts: Contact[]): boolean {
  return site.isActive && !hasPlantManager(contacts, site.siteNumber);
}

// ---- Asset list ----

export interface AssetGroup {
  location: string;
  assets: AssetRow[];
}

/**
 * Grouped by location as the Sheet names them, in name order, IDs in order inside.
 * Search matches Asset ID and equipment type across every location. Removed assets stay off.
 */
export function groupAssets(assets: AssetRow[], search = ""): AssetGroup[] {
  const q = search.trim().toLowerCase();
  const groups = new Map<string, AssetRow[]>();
  for (const a of assets) {
    if (a.isRemoved) continue;
    if (q && !a.assetId.toLowerCase().includes(q) && !(a.equipmentType ?? "").toLowerCase().includes(q)) continue;
    const loc = a.location?.trim() || "No location";
    const list = groups.get(loc) ?? [];
    list.push(a);
    groups.set(loc, list);
  }
  return [...groups.entries()]
    .sort(([a], [b]) => a.localeCompare(b, "en", { numeric: true }))
    .map(([location, list]) => ({
      location,
      assets: list.sort((x, y) => x.assetId.localeCompare(y.assetId, "en", { numeric: true })),
    }));
}

export function locationCount(assets: AssetRow[]): number {
  return groupAssets(assets).length;
}

// ---- Demo set ----

export const DEMO_TEMPLATES: { key: "mcc" | "msd" | "panel"; name: string }[] = [
  { key: "mcc", name: "MCC Bucket" },
  { key: "msd", name: "MSD" },
  { key: "panel", name: "Panel" },
];

// ---- Edits: the same checks run on the page and on the server ----

export interface SiteDetailsInput {
  name: string;
  city: string;
  state: string;
  /** YYYY-MM-DD or blank to clear. */
  arcFlashAssessedOn: string;
  /** One of SITE_ZONES. Blank or absent: the saved zone stays as it is. */
  timeZone?: string;
}

const DATE = /^\d{4}-\d{2}-\d{2}$/;

export function siteDetailsProblem(input: SiteDetailsInput, today: string): string | null {
  if (!input.name.trim()) return "Enter the site name.";
  if (input.name.trim().length > 60) return "Keep the site name under 60 characters.";
  if (!input.city.trim()) return "Enter the city.";
  if (input.city.trim().length > 60) return "Keep the city under 60 characters.";
  if (!/^[A-Za-z]{2}$/.test(input.state.trim())) return "State is the two-letter code, like KY.";
  const d = input.arcFlashAssessedOn.trim();
  if (d) {
    if (!DATE.test(d) || Number.isNaN(Date.parse(`${d}T00:00:00Z`))) return "Pick a real date for Arc Flash Assessed.";
    if (d > today) return "Arc Flash Assessed can't be in the future.";
  }
  const z = (input.timeZone ?? "").trim();
  if (z && !isSiteZone(z)) return "Pick the plant's time zone from the list.";
  return null;
}

/**
 * Change Time Zone saves the zone alone (NAV_CRAWL_WORDS.md A1). The other details are not checked here,
 * so a site saved before city and state were required can still take its zone.
 */
export function zoneOnlyProblem(timeZone: string): string | null {
  return isSiteZone(timeZone.trim()) ? null : "Pick the plant's time zone from the list.";
}

export function cleanSiteDetails(input: SiteDetailsInput): SiteDetailsInput {
  return {
    name: input.name.trim(),
    city: input.city.trim(),
    state: input.state.trim().toUpperCase(),
    arcFlashAssessedOn: input.arcFlashAssessedOn.trim(),
    ...((input.timeZone ?? "").trim() ? { timeZone: (input.timeZone ?? "").trim() } : {}),
  };
}

// ---- Add Site (ADD_SITE_WORDS.md, approved 2026-10-23) ----
// One tap makes the site record (Not Active) and its 99 Unclaimed QEW bag tags, together or not at all.
// Server code then builds the Drive folder, copies the standard workbook and the page saves the tag PDF.

export interface NewSiteInput {
  siteNumber: string;
  name: string;
  city: string;
  state: string;
  /** The plant's own clock. Add Site fills it from a one-zone state; a two-zone state waits for a pick. */
  timeZone?: string;
}

export const QEW_TAG_COUNT = 99;

/** The same checks as Change Details, plus a free two-digit number. */
export function newSiteProblem(input: NewSiteInput, taken: Iterable<string>): string | null {
  const n = input.siteNumber.trim();
  if (!isSiteNumber(n)) return "Site number is two digits, 00 to 99.";
  if ([...taken].includes(n)) return `Site ${n} is already a site.`;
  const details = siteDetailsProblem({ name: input.name, city: input.city, state: input.state, arcFlashAssessedOn: "" }, "9999-12-31");
  if (details) return details;
  if (!isSiteZone((input.timeZone ?? "").trim())) return "Pick the plant's time zone.";
  return null;
}

export function cleanNewSite(input: NewSiteInput): NewSiteInput {
  const d = cleanSiteDetails({ name: input.name, city: input.city, state: input.state, arcFlashAssessedOn: "" });
  return { siteNumber: input.siteNumber.trim(), name: d.name, city: d.city, state: d.state, timeZone: (input.timeZone ?? "").trim() };
}

/** NN-QEW01 to NN-QEW99, the same IDs safetag_qew_tags.py prints. */
export function qewTagIds(siteNumber: string): string[] {
  return Array.from({ length: QEW_TAG_COUNT }, (_, i) => `${siteNumber}-QEW${String(i + 1).padStart(2, "0")}`);
}

/** A printed tag nobody has claimed yet. The claim page fills in the rest at class. */
export function unclaimedBadge(tagId: string, siteNumber: string): Record<string, unknown> {
  return {
    "Badge ID": tagId,
    "Badge Type": "QEW",
    "Person ID": "",
    "Holder Name": "",
    Sites: [siteNumber],
    Authorities: [],
    Status: "Unclaimed",
    "Issued On": "",
    "Disabled On": null,
  };
}

/** Plants in Lists that are not sites yet: the chips on the Add Site sheet. */
export function plantsNotSites<T extends { siteNumber: string }>(plants: readonly T[], sites: readonly { siteNumber: string }[]): T[] {
  const have = new Set(sites.map((x) => x.siteNumber));
  return plants.filter((p) => !have.has(p.siteNumber)).sort((a, b) => a.siteNumber.localeCompare(b.siteNumber));
}

export interface ContactInput {
  name: string;
  email: string;
  phone: string;
  role: string;
  isActive: boolean;
  usedFor: UsedFor;
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * keepRole: the role already on the record. A role copied from the Sheet ("Safety")
 * stays allowed on that contact until someone picks one from the list.
 */
export function contactProblem(input: ContactInput, keepRole = ""): string | null {
  if (!input.name.trim()) return "Enter the contact's name.";
  if (input.name.trim().length > 80) return "Keep the name under 80 characters.";
  if (!EMAIL.test(input.email.trim())) return "Enter a working email address.";
  if (input.phone.trim().length > 30) return "Keep the phone number under 30 characters.";
  const role = input.role.trim();
  if (!role) return "Pick a role.";
  if (!(CONTACT_ROLES as readonly string[]).includes(role) && role !== keepRole) return "Pick a role from the list.";
  return null;
}

export function cleanContact(input: ContactInput): ContactInput {
  return {
    name: input.name.trim(),
    email: input.email.trim().toLowerCase(),
    phone: input.phone.trim(),
    role: input.role.trim(),
    isActive: input.isActive,
    usedFor: {
      alerts: Boolean(input.usedFor.alerts),
      escalation: Boolean(input.usedFor.escalation),
      eewpApprover: Boolean(input.usedFor.eewpApprover),
      eewpAwareness: Boolean(input.usedFor.eewpAwareness),
      lotoAwareness: Boolean(input.usedFor.lotoAwareness),
      lotoVerifier: Boolean(input.usedFor.lotoVerifier),
    },
  };
}

/** The same person twice at one site is refused. Matched on email. */
export function duplicateContact(contacts: Contact[], siteNumber: string, email: string, exceptId = ""): Contact | null {
  const e = email.trim().toLowerCase();
  return siteContacts(contacts, siteNumber).find((c) => c.contactId !== exceptId && c.email.toLowerCase() === e) ?? null;
}

/** Next CON number across program and site contacts. CON-0001 style, as the copy scripts wrote them. */
export function nextContactId(existingIds: string[]): string {
  let high = 0;
  for (const id of existingIds) {
    const m = /^CON-(\d+)$/.exec(id);
    if (m) high = Math.max(high, Number(m[1]));
  }
  return `CON-${String(high + 1).padStart(4, "0")}`;
}

export function isSiteNumber(v: unknown): v is string {
  return typeof v === "string" && /^\d{2}$/.test(v);
}
